import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { adminService } from '@/services/admin';
import type { 
  AdminState, 
  Tenant, 
  ApiKey, 
  Link,
  CreateTenantRequest,
  CreateApiKeyRequest,
  PaginationParams 
} from '@/types';

export const useAdminStore = defineStore('admin', () => {
  // State
  const tenants = ref<Tenant[]>([]);
  const apiKeys = ref<ApiKey[]>([]);
  const globalLinks = ref<Link[]>([]);
  const globalTopLinks = ref<Link[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Computed
  const adminState = computed<AdminState>(() => ({
    tenants: tenants.value,
    apiKeys: apiKeys.value,
    globalLinks: globalLinks.value,
    globalTopLinks: globalTopLinks.value,
    isLoading: isLoading.value,
    error: error.value,
  }));

  const activeTenants = computed(() => 
    tenants.value.filter(tenant => tenant.isActive)
  );

  const activeApiKeys = computed(() => 
    apiKeys.value.filter(key => key.isActive)
  );

  // Actions
  const clearError = () => {
    error.value = null;
  };

  const setError = (message: string) => {
    error.value = message;
  };

  // Tenant Management
  const fetchTenants = async () => {
    try {
      isLoading.value = true;
      clearError();

      const tenantList = await adminService.listTenants();
      tenants.value = Array.isArray(tenantList) ? tenantList : [];
    } catch (err: any) {
      setError(err.message || 'Failed to fetch tenants');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const createTenant = async (data: CreateTenantRequest) => {
    try {
      isLoading.value = true;
      clearError();

      const newTenant = await adminService.createTenant(data);
      tenants.value.push(newTenant);
      return newTenant;
    } catch (err: any) {
      setError(err.message || 'Failed to create tenant');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // API Key Management
  const fetchTenantApiKeys = async (tenantId: string) => {
    try {
      isLoading.value = true;
      clearError();

      const keyList = await adminService.listTenantApiKeys(tenantId);
      apiKeys.value = keyList;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch API keys');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const createTenantApiKey = async (tenantId: string, data: CreateApiKeyRequest) => {
    try {
      isLoading.value = true;
      clearError();

      const newApiKey = await adminService.createTenantApiKey(tenantId, data);
      apiKeys.value.push(newApiKey);
      return newApiKey;
    } catch (err: any) {
      setError(err.message || 'Failed to create API key');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };



  const fetchAdminApiKeys = async () => {
    try {
      isLoading.value = true;
      clearError();

      const keyList = await adminService.listAdminApiKeys();
      apiKeys.value = keyList;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch admin API keys');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const createAdminApiKey = async (data: CreateApiKeyRequest) => {
    try {
      isLoading.value = true;
      clearError();

      const newApiKey = await adminService.createAdminApiKey(data);
      apiKeys.value.push(newApiKey);
      return newApiKey;
    } catch (err: any) {
      setError(err.message || 'Failed to create admin API key');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };



  // Global Link Management
  const fetchGlobalLinks = async (params: PaginationParams = {}) => {
    try {
      isLoading.value = true;
      clearError();

      const response = await adminService.listGlobalLinks(params);
      globalLinks.value = response.data;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch global links');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchGlobalTopLinks = async (limit: number = 10) => {
    try {
      clearError();
      const response = await adminService.getGlobalTopLinks(limit);
      globalTopLinks.value = response.data;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch global top links');
      throw err;
    }
  };

  return {
    // State
    tenants,
    apiKeys,
    globalLinks,
    globalTopLinks,
    isLoading,
    error,
    
    // Computed
    adminState,
    activeTenants,
    activeApiKeys,
    
    // Actions
    clearError,
    setError,
    fetchTenants,
    createTenant,
    fetchTenantApiKeys,
    createTenantApiKey,
    fetchAdminApiKeys,
    createAdminApiKey,
    fetchGlobalLinks,
    fetchGlobalTopLinks,
  };
});

